import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { AppShell } from "./app-shell/AppShell";
import { ShellError } from "./app-shell/components/ShellError";
import { ShellPending } from "./app-shell/components/ShellPending";
import { NotFoundPage } from "./app-shell/pages/NotFoundPage";
import { MyWorkPage } from "./app-shell/pages/MyWorkPage";
import { NotificationsPage } from "./app-shell/pages/NotificationsPage";
import { SearchPage } from "./app-shell/pages/SearchPage";
import { hasSession } from "./lib/auth/authStore";
import { gleanReviewIdFromReturnTo } from "./lib/auth/gleanReviewReturnTo";

const Login = lazyRouteComponent(() => import("./pages/Login"), "Login");
const Home = lazyRouteComponent(() => import("./pages/Home"), "Home");
const ScopeHome = lazyRouteComponent(
  () => import("./app-shell/pages/ScopeHome"),
  "ScopeHome",
);
const ClientHome = lazyRouteComponent(
  () => import("./app-shell/pages/ClientHome"),
  "ClientHome",
);
const NodeSection = lazyRouteComponent(
  () => import("./app-shell/pages/NodeSection"),
  "NodeSection",
);
const SubAppPage = lazyRouteComponent(
  () => import("./app-shell/pages/SubAppPage"),
  "SubAppPage",
);
const ProfilePage = lazyRouteComponent(
  () => import("./app-shell/pages/ProfilePage"),
  "ProfilePage",
);
const DataPage = lazyRouteComponent(
  () => import("./app-shell/pages/DataPage"),
  "DataPage",
);
const AssetsView = lazyRouteComponent(
  () => import("./app-shell/pages/AssetsView"),
  "AssetsView",
);
const LegacyPerlabRoute = lazyRouteComponent(
  () => import("./app-shell/pages/LegacyPerlabRoute"),
  "LegacyPerlabRoute",
);
const ActivityPage = lazyRouteComponent(
  () => import("./app-shell/pages/scoped/ActivityPage"),
  "ActivityPage",
);
const KnowledgePage = lazyRouteComponent(
  () => import("./app-shell/pages/scoped/KnowledgePage"),
  "KnowledgePage",
);
const AppsPage = lazyRouteComponent(
  () => import("./app-shell/pages/scoped/AppsPage"),
  "AppsPage",
);
const EngagementsPage = lazyRouteComponent(
  () => import("./app-shell/pages/scoped/EngagementsPage"),
  "EngagementsPage",
);
const DecisionsPage = lazyRouteComponent(
  () => import("./app-shell/pages/scoped/DecisionsPage"),
  "DecisionsPage",
);
const IntelPage = lazyRouteComponent(
  () => import("./app-shell/pages/scoped/IntelPage"),
  "IntelPage",
);
const GovernancePage = lazyRouteComponent(
  () => import("./app-shell/pages/scoped/GovernancePage"),
  "GovernancePage",
);
const UserSettingsPage = lazyRouteComponent(
  () => import("./app-shell/pages/SettingsPage"),
  "UserSettingsPage",
);
const GleanReviewPage = lazyRouteComponent(
  () => import("./app-shell/glean/GleanReviewPage"),
  "GleanReviewPage",
);
const WorkspaceSettingsPage = lazyRouteComponent(
  () => import("./app-shell/pages/SettingsPage"),
  "WorkspaceSettingsPage",
);
const AdminPage = lazyRouteComponent(
  () => import("./app-shell/pages/AdminPage"),
  "AdminPage",
);
const UserManagementPage = lazyRouteComponent(
  () => import("./app-shell/pages/UserManagementPage"),
  "UserManagementPage",
);
const ClientPortalModulePage = lazyRouteComponent(
  () => import("./app-shell/pages/ClientPortalModulePage"),
  "ClientPortalModulePage",
);
const MissionControlPage = lazyRouteComponent(
  () => import("./app-shell/pages/MissionControlPage"),
  "MissionControlPage",
);
const SandboxPage = lazyRouteComponent(
  () => import("./app-shell/pages/SandboxPage"),
  "SandboxPage",
);

const rootRoute = createRootRoute({
  component: Outlet,
});

// Unauthenticated — sibling of the shell (reuses the existing Login/auth/SSO).
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: Login,
});

// Pathless layout route — the AppShell chrome wraps every authenticated
// scoped route. URL contract (under basepath /workspace):
//   /w/$ws/home · /w/$ws/e/$eng/home · /w/$ws/e/$eng/p/$project/home
//   /w/$ws/settings · /w/$ws/e/$eng/p/$project/apps/$app
const appShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "appShell",
  // Phase 0.5 — session-presence gate (runs before the shell renders → no flash).
  // requireScope() seam (5-type role / project membership) is a later authz wave.
  // clientAccessTier is enforced in SubAppPage.tsx, not here — see authStore.ts.
  beforeLoad: ({ location }) => {
    if (!hasSession()) {
      const returnTo = `/workspace${location.pathname}`;
      if (gleanReviewIdFromReturnTo(returnTo)) {
        throw redirect({ to: "/login", search: () => ({ returnTo }) });
      }
      throw redirect({ to: "/login" });
    }
  },
  component: AppShell,
});

// The authenticated root is a product chooser. AppShell renders this route without
// workspace chrome so users choose Workspace or SandBox before entering either product.
const indexRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/",
  component: Home,
});

const workspaceHomeRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/home",
  component: ScopeHome,
});

// Client home — first-class client level (ADR-08 / Decision A). Redesigned dashboard (KPI strip +
// rich engagement/project cards, 2 layout variants) — client-only; every other node level (workspace/
// engagement/project below) keeps the original generic ScopeHome/NodeContent list view unchanged.
const clientHomeRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/c/$client/home",
  component: ClientHome,
});

const engagementHomeRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/c/$client/e/$eng/home",
  component: ScopeHome,
});

const projectHomeRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/c/$client/e/$eng/p/$project/home",
  component: ScopeHome,
});

// Row 3.6 — was a placeholder pointing at ScopeHome; swapped to the real settings-shell page
// (TabbedSectionsTemplate, CAT22). Real per-tab CONTENT is CAT31-backend-dependent — CFR'd.
const settingsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/settings",
  component: WorkspaceSettingsPage,
});

// Row 3.5 — user-level settings (`/me/settings`). Not workspace-scoped (no `$ws` param) since it's
// account-level, not tenant-level.
const meSettingsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/me/settings",
  component: UserSettingsPage,
});
const gleanReviewRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/glean/review/$reviewId",
  component: GleanReviewPage,
});

// Row 3.8 — admin landing route. No genuine admin-dashboard content exists anywhere in this
// codebase (see AdminPage.tsx's own header comment) — placeholder shell only.
const adminRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/admin",
  component: AdminPage,
});

const userManagementRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/admin/users",
  component: UserManagementPage,
});

const missionControlRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/admin/mission-control",
  component: MissionControlPage,
});

const sandboxRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/sandbox",
  component: SandboxPage,
});

// Row 3.9 — client-portal-per-module, nested under the canonical `/w/$ws/c/$client/...` family
// (see ClientPortalModulePage.tsx's own header comment for the addressing-scheme reasoning).
const clientPortalModuleRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/c/$client/portal/$moduleBase",
  component: ClientPortalModulePage,
});

// Row 3.3 — module-scoped routing. The master spec's own as-built cell flags today's real
// architecture as scope-nested app-mounting (`/apps/$app`), not a bare top-level `/modules/`
// path (which would fork a third addressing scheme, against the locked 48.24 §6 rule). This is a
// thin, additive REDIRECT alias — `/w/$ws/modules/$moduleBase` resolves immediately to the
// canonical `/w/$ws/apps/$app` mount, giving the shorter/more-discoverable URL form real
// resolution without inventing a second mounting mechanism.
const workspaceModulesAliasRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/modules/$moduleBase",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/w/$ws/apps/$app",
      params: { ws: params.ws, app: params.moduleBase },
    });
  },
});

// Row 3.13 — legacy `:rg`->`:workspace` redirect. TWO conflicting locked decisions were found for
// this row: the master spec's own "v3 lock #6" text says legacy `:rg` URLs return HTTP 308
// (`00_VW_MASTER_SPEC.md:140,156`), but the MORE RECENT ENGSCOPE 9.4 build-time decision
// (`_SENTINEL/knowledge/23_cat9_engscope_batch_plan_2026-07-03.md:14`, closeout `24_...:33`)
// canonizes "FE-redirect-not-308 (SPA constraint)" — a client-side SPA architecturally cannot
// emit a real HTTP status on a browser-history navigation. This build follows the more recent,
// implementation-aware ENGSCOPE correction (a client-side `beforeLoad`/`redirect()`, matching the
// existing `indexRoute` pattern above) rather than the earlier spec lock, which predates the SPA
// architecture decision it now contradicts. A single splat route captures the whole legacy
// sub-path and re-emits it under the current `/w/$ws/...` shape, rather than enumerating every
// individual legacy route.
const legacyRgRedirectRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/rg/$rg/$",
  beforeLoad: ({ params }) => {
    // Post-build review (MED, fixed): `to` here is a raw string, not a `$param` template + params
    // object, so TanStack's own param encoder never runs on it — an unescaped `#`/`?`/space in
    // `rg` or the splat would corrupt the resulting path's query/hash boundary. Each segment is
    // re-encoded individually (splitting the splat on "/", encoding every piece, rejoining) rather
    // than splicing the decoded raw values straight into the target string.
    const restSegments = params._splat ? params._splat.split("/").map(encodeURIComponent) : [];
    const rest = restSegments.length > 0 ? `/${restSegments.join("/")}` : "";
    throw redirect({ to: `/w/${encodeURIComponent(params.rg)}${rest}` as never });
  },
});

// MyWork — workspace-level cross-engagement landing (app-independent).
const myWorkRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/mywork",
  component: MyWorkPage,
});

// Search — workspace-level content search (app-independent).
const searchRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/search",
  component: SearchPage,
});

// Notifications — workspace-level deliver surface (app-independent, Cat 32).
const notificationsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/notifications",
  component: NotificationsPage,
});

// Profile / account — workspace-level account surface (app-independent, Cat 30).
const profileRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/profile",
  component: ProfilePage,
});

// ── Scope-aware destinations (shell rail, 2026-06-29). Each = a real endpoint-backed page;
//    the ethical wall is enforced server-side via the reader/requester scope param. ──
// Workspace-level:
const workspaceDataRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/data",
  component: AssetsView,
});
const workspaceActivityRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/activity",
  component: ActivityPage,
});
const workspaceKnowledgeRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/knowledge",
  component: KnowledgePage,
});
// Upper-scope bookmarks retain the governed catalog; Project aliases open Project Data Intelligence.
// Project aliases mirror both project URL families; the generic node route is dispatched by NodeSection.
const workspaceKearneyIpRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/kearney-ip",
  component: LegacyPerlabRoute,
});
const clientKearneyIpRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/c/$client/kearney-ip",
  component: LegacyPerlabRoute,
});
const engagementKearneyIpRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/c/$client/e/$eng/kearney-ip",
  component: LegacyPerlabRoute,
});
const workspaceEngagementKearneyIpRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/e/$eng/kearney-ip",
  component: LegacyPerlabRoute,
});
// Client-level (wall-scoped):
const clientEngagementsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/c/$client/engagements",
  component: EngagementsPage,
});
const clientDataRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/c/$client/data",
  component: DataPage,
});
const clientDecisionsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/c/$client/decisions",
  component: DecisionsPage,
});
const clientIntelRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/c/$client/intel",
  component: IntelPage,
});
const clientGovernanceRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/c/$client/governance",
  component: GovernancePage,
});

// ── B4: consistent core Sections at EVERY level. The pages read the DEEPEST present scope
//    (useScopeNodeId) and pass it as the reader/requester id, so each shows the CURRENT node's
//    data, wall-filtered server-side. Workspace gains Decisions/Intel/Governance; client gains
//    Activity; engagement + project gain the full core set (Data/Decisions/Intel/Activity/Governance).
//    Data at workspace stays AssetsView (the cross-client admin view); deeper Data uses DataPage. ──
const workspaceDecisionsRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/decisions", component: DecisionsPage });
const workspaceIntelRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/intel", component: IntelPage });
const workspaceGovernanceRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/governance", component: GovernancePage });
const clientActivityRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/activity", component: ActivityPage });
// Client gains the full facet set (operator 2026-07-03: client = engagement/project) — Knowledge + Apps
// complete the set (the other client facets already existed). Empty scopes render the shared EmptyState.
const clientKnowledgeRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/knowledge", component: KnowledgePage });
const clientAppsRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/apps", component: AppsPage });

const engDataRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/e/$eng/data", component: DataPage });
const engDecisionsRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/e/$eng/decisions", component: DecisionsPage });
const engIntelRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/e/$eng/intel", component: IntelPage });
const engActivityRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/e/$eng/activity", component: ActivityPage });
const engGovernanceRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/e/$eng/governance", component: GovernancePage });

// ── Nav-restructure (knowledge/07, cat 48): the facet tab row adds Knowledge + an Apps STATUS
//    dashboard at engagement/project (level-dependent facet map — facets/registry.ts). Knowledge
//    reuses the honest placeholder page (scope-neutral); "/apps" (no param) coexists with the
//    "/apps/$app" mount routes (exact-segment match beats the param). ──
const engKnowledgeRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/e/$eng/knowledge", component: KnowledgePage });
const engAppsRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/e/$eng/apps", component: AppsPage });
const projKnowledgeRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/e/$eng/p/$project/knowledge", component: KnowledgePage });
const projAppsRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/e/$eng/p/$project/apps", component: AppsPage });

const projDataRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/e/$eng/p/$project/data", component: DataPage });
const projDecisionsRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/e/$eng/p/$project/decisions", component: DecisionsPage });
const projIntelRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/e/$eng/p/$project/intel", component: IntelPage });
const projActivityRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/e/$eng/p/$project/activity", component: ActivityPage });
const projGovernanceRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/e/$eng/p/$project/governance", component: GovernancePage });
const projKearneyIpRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/c/$client/e/$eng/p/$project/kearney-ip", component: LegacyPerlabRoute });

// ── A5/A6/B7: a CLIENT-LESS engagement (cross-client host — mounts at the workspace, no client
//    segment). Same home/section/app surfaces as a client-scoped engagement, addressed without /c.
//    The page scope reads the deepest URL segment (useScopeNodeId / scalars), so they Just Work. ──
const wsEngHomeRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/home", component: ScopeHome });
const wsEngDataRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/data", component: DataPage });
const wsEngDecisionsRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/decisions", component: DecisionsPage });
const wsEngIntelRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/intel", component: IntelPage });
const wsEngActivityRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/activity", component: ActivityPage });
const wsEngGovernanceRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/governance", component: GovernancePage });
const wsEngKnowledgeRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/knowledge", component: KnowledgePage });
const wsEngAppsRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/apps", component: AppsPage });
const wsEngAppRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/apps/$app", component: SubAppPage });

// ── 48.23 (eng-anchored Projects): a PROJECT inside a client-less HOST engagement. Projects live
//    ONLY inside engagements (either kind — guard 202607060017); this family gives the host kind
//    its project surface, mirroring the client-pathed project facets without the /c segment. ──
const wsEngProjHomeRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/p/$project/home", component: ScopeHome });
const wsEngProjDataRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/p/$project/data", component: DataPage });
const wsEngProjDecisionsRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/p/$project/decisions", component: DecisionsPage });
const wsEngProjIntelRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/p/$project/intel", component: IntelPage });
const wsEngProjActivityRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/p/$project/activity", component: ActivityPage });
const wsEngProjGovernanceRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/p/$project/governance", component: GovernancePage });
const wsEngProjKnowledgeRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/p/$project/knowledge", component: KnowledgePage });
const wsEngProjAppsRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/p/$project/apps", component: AppsPage });
const wsEngProjKearneyIpRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/p/$project/kearney-ip", component: LegacyPerlabRoute });
const wsEngProjAppRoute = createRoute({ getParentRoute: () => appShellRoute, path: "/w/$ws/e/$eng/p/$project/apps/$app", component: SubAppPage });

// Sub-app leaf — PROJECT-level mount. Artifact-path splat is added when SubAppPage hosts a real
// app (Phase 1 native / Phase 2 iframe).
const subAppRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/c/$client/e/$eng/p/$project/apps/$app",
  component: SubAppPage,
});

// V2 multi-level mounting — an app can mount at ANY tenant level, not just project. These mirror the
// per-level home routes; `apps` is a distinct literal segment so there's no collision with home/mywork/etc.
// The mount scope = the deepest segment present; SubAppPage + useUrlScopeSync resolve it (ContextKey state
// is already level-agnostic), and useScopeData reads at that level. Which levels an app MAY mount at is the
// registry `mountLevels` declaration (enforced at the launch/nav affordance + SubAppPage, not the route).
const workspaceAppRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/apps/$app",
  component: SubAppPage,
});

const clientAppRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/c/$client/apps/$app",
  component: SubAppPage,
});

const engagementAppRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/c/$client/e/$eng/apps/$app",
  component: SubAppPage,
});

// ── Generic node-addressable family (spec 48.24 §6): /w/$ws/n/$node[/home | /$section | /apps/$app].
//    A node addressed by ID → free-shape safe (a client under an engagement, a project under a client
//    get a lossless URL the positional typed routes cannot encode). The ~50 typed routes above remain
//    deep-link ALIASES; shell-emitted navigation moves to navTo.nodeHome/nodeSection. Pages read the
//    $node-aware useScopeNodeId, so they render the current node identically to the typed alias.
//    Exact segments (home, apps) beat the $section param (TanStack match precedence). ──
const nodeHomeRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/n/$node/home",
  component: ScopeHome,
});
const nodeAppRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/n/$node/apps/$app",
  component: SubAppPage,
});
const nodeSectionRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "/w/$ws/n/$node/$section",
  component: NodeSection,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  appShellRoute.addChildren([
    indexRoute,
    workspaceHomeRoute,
    clientHomeRoute,
    engagementHomeRoute,
    projectHomeRoute,
    settingsRoute,
    myWorkRoute,
    searchRoute,
    notificationsRoute,
    profileRoute,
    meSettingsRoute,
    gleanReviewRoute,
    adminRoute,
    clientPortalModuleRoute,
    userManagementRoute,
    workspaceModulesAliasRoute,
    legacyRgRedirectRoute,
    missionControlRoute,
    sandboxRoute,
    workspaceDataRoute,
    workspaceActivityRoute,
    workspaceKnowledgeRoute,
    workspaceKearneyIpRoute,
    clientKearneyIpRoute,
    engagementKearneyIpRoute,
    workspaceEngagementKearneyIpRoute,
    clientEngagementsRoute,
    clientDataRoute,
    clientDecisionsRoute,
    clientIntelRoute,
    clientGovernanceRoute,
    // B4 — consistent core Sections at every level
    workspaceDecisionsRoute,
    workspaceIntelRoute,
    workspaceGovernanceRoute,
    clientActivityRoute,
    clientKnowledgeRoute,
    clientAppsRoute,
    engDataRoute,
    engDecisionsRoute,
    engIntelRoute,
    engActivityRoute,
    engGovernanceRoute,
    engKnowledgeRoute,
    engAppsRoute,
    projDataRoute,
    projDecisionsRoute,
    projIntelRoute,
    projActivityRoute,
    projGovernanceRoute,
    projKnowledgeRoute,
    projAppsRoute,
    projKearneyIpRoute,
    // client-less engagement (cross-client host)
    wsEngHomeRoute,
    wsEngDataRoute,
    wsEngDecisionsRoute,
    wsEngIntelRoute,
    wsEngActivityRoute,
    wsEngGovernanceRoute,
    wsEngKnowledgeRoute,
    wsEngAppsRoute,
    wsEngAppRoute,
    // 48.23 — project inside a client-less host engagement
    wsEngProjHomeRoute,
    wsEngProjDataRoute,
    wsEngProjDecisionsRoute,
    wsEngProjIntelRoute,
    wsEngProjActivityRoute,
    wsEngProjGovernanceRoute,
    wsEngProjKnowledgeRoute,
    wsEngProjAppsRoute,
    wsEngProjKearneyIpRoute,
    wsEngProjAppRoute,
    workspaceAppRoute,
    clientAppRoute,
    engagementAppRoute,
    subAppRoute,
    // Generic node-addressable family (§6) — coexists with the typed aliases above.
    nodeHomeRoute,
    nodeAppRoute,
    nodeSectionRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  basepath: "/workspace",
  // Phase-0 acceptance states: defined error boundary + loading skeleton for
  // every route (no white screen / no blank flash).
  defaultErrorComponent: ShellError,
  defaultPendingComponent: ShellPending,
  // Row 3.10 — a truly-unmatched route renders the same generic "not available" surface as a
  // wall-sealed resource (NotFoundPage.tsx), since the backend deliberately makes those two cases
  // indistinguishable at the data layer — the shell shouldn't pretend otherwise at the routing layer.
  defaultNotFoundComponent: NotFoundPage,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
